import {
    Box,
    Card,
    CardContent,
    IconButton,
    Rating,
    Tooltip,
    Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PublicOffIcon from "@mui/icons-material/PublicOff";
import PublicIcon from '@mui/icons-material/Public';
import React, { useMemo, useState } from "react";
import moment from "moment";
import { Story } from "../../../services/restserver-openapi";
import { useAppSelector } from "../../../hooks/hooks";
import ImageCarouselDialog from "../../../components/presentationalComponents/ImageCarousel";
import CommunityUserLink from "./ProfileLink";

interface StoryCardProps {
    story: Story;
    isOwner: boolean;
    showOwner: boolean;
    onEdit: (report: Story) => void;
    onDelete: () => void;
}

export default function StoryCard({
                                      story,
                                      isOwner,
                                      showOwner,
                                      onEdit,
                                      onDelete,
                                  }: StoryCardProps) {
    const [imageViewerOpen, setImageViewerOpen] = useState(false);
    const userEntitlements = useAppSelector(state => state.user.entitlements);

    const canCreateContent = useMemo(() => {
        return userEntitlements.some(entitlement => entitlement.featureId === 'vanlifercreatecontent' && entitlement.value === "true")
    }, [userEntitlements]);

    return (
        <>
            <Card
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    mb: 3,
                    overflow: 'hidden',
                    position: 'relative',
                }}
            >
                {/* Top Bar */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        px: 2,
                        py: 1,
                        borderBottom: '1px solid #e0e0e0',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {isOwner && (
                            story.sharingMode === 'PRIVATE' ? (
                                <PublicOffIcon fontSize="small" color="disabled" />
                            ) : (
                                <PublicIcon fontSize="small" color="disabled" />
                            )
                        )}
                        <Typography variant="caption" color="text.secondary">
                            {moment(story.storyDate).format('DD.MM.YYYY')}
                            {showOwner && (
                                <>
                                    {' · '}
                                    <CommunityUserLink
                                        communityName={story.owner ?? "Unbekannter User"}
                                    />
                                </>
                            )}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {isOwner && (
                            <>
                                <Tooltip title={canCreateContent ? "Bearbeiten" : "Fehlende Berechtigung"}>
                                    <span>
                                        <IconButton
                                            disabled={!canCreateContent}
                                            size="small"
                                            onClick={() => onEdit(story)}
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>

                                <Tooltip title="Löschen">
                                    <IconButton
                                        size="small"
                                        onClick={onDelete}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </>
                        )}
                    </Box>
                </Box>

                {/* Image preview */}
                {story.storyImages && story.storyImages?.length > 0 && story.storyImages[0].sasDownloadLink && (
                    <Box
                        sx={{
                            width: '100%',
                            maxHeight: '66vh',
                            overflow: 'hidden',
                            position: 'relative',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                        onClick={() => setImageViewerOpen(true)}
                    >
                        <Box
                            component="img"
                            src={story.storyImages[0].sasDownloadLink}
                            alt="Story-Bild"
                            sx={{
                                maxHeight: '100%',
                                maxWidth: '100%',
                                objectFit: 'contain',
                            }}
                        />
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: 8,
                                right: 8,
                                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                color: 'white',
                                borderRadius: '12px',
                                px: 1.2,
                                py: 0.5,
                                fontSize: '0.75rem',
                            }}
                        >
                            {story.storyImages.length} {story.storyImages.length === 1 ? 'Bild' : 'Bilder'}
                        </Box>
                    </Box>
                )}

                {/* Content */}
                <CardContent sx={{ pt: 2, pb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 'bold',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                            }}
                        >
                            {story.title || 'Ohne Titel'}
                        </Typography>
                        <Rating value={story.rating} precision={0.5} readOnly size="small" />
                    </Box>

                    {story.content && (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word'
                            }}
                        >
                            {story.content}
                        </Typography>
                    )}
                </CardContent>
            </Card>

            {/* Image Carousel */}
            <ImageCarouselDialog
                open={imageViewerOpen}
                onClose={() => setImageViewerOpen(false)}
                images={story.storyImages?.map(img => img.sasDownloadLink!)}
            />
        </>
    );
};
